/**
 * Thermal optic — hostiles read as heat while the optic is up.
 *
 * The single rule that decides whether this is an optic or a cheat: **it must obey
 * occlusion.** A highlight drawn through a wall is a wallhack with a scope model on
 * it, and no amount of tuning makes that a fair attachment.
 *
 * The implementation gets occlusion exactly right, per pixel, for free: while the
 * optic is up, each hostile's own meshes are swapped to a shared emissive material
 * and swapped back when it comes down. Depth testing is untouched, so a soldier
 * behind a wall is hidden by that wall the way he always was, and a soldier whose
 * head is over a crate shows exactly the part of him that is over the crate. No
 * proxy geometry, no screen-space marker, no per-hitbox visibility guessing — the
 * depth buffer already knows the answer.
 *
 * Two gates keep it honest beyond that:
 *
 *  - **Cone.** Only hostiles inside the optic's half-angle are painted. A thermal
 *    scope shows what it is pointed at; letting the whole screen light up would
 *    hand the player peripheral intel the device does not have.
 *  - **Sight.** The optic has to be *up*. It reads `WeaponSystem.adsProgress`, the
 *    animated value, not the ADS button — the same distinction that module makes
 *    everywhere else, and for the same reason: the button is instant and the glass
 *    is not.
 *
 * ## The smoke rule, chosen and documented
 *
 * **Thermal defeats smoke; walls defeat thermal.**
 *
 * That is the real property of the device and it is the more interesting of the two
 * options: it gives the smoke launcher and the thermal optic a genuine relationship
 * rather than making them redundant. Pop smoke and push, and you can see hostiles
 * who provably cannot see you — `SmokeField` blocks `physics.visible`, which is
 * what every AI perception check funnels through, so the asymmetry is real in both
 * directions rather than being a claim in a comment.
 *
 * It is implemented by drawing the highlight in the transparent queue at a high
 * render order. Smoke puffs are soft particles that do not write depth, so they
 * cannot occlude anything; drawing the silhouette after them puts the heat source
 * *through* the cloud. World geometry does write depth, so it still cuts the
 * silhouette off — which is precisely the pair of behaviours the rule asks for.
 */

import * as THREE from 'three';

export const TUNING = {
  /** ADS progress at which the optic is considered up. */
  adsThreshold: 0.55,
  /** Render order for the highlight. Must beat the additive particle batch (11)
   *  so smoke cannot draw over a heat source. */
  renderOrder: 24,
  /**
   * Core colour, in scene-linear. Kept well clear of the shoulder: pushed much
   * above 1.5 the ACES curve desaturates it toward white and the silhouette stops
   * reading as *hot* and starts reading as *blown out*, which is much harder to
   * pick out against a bright sky.
   */
  colour: [1.45, 0.20, 0.035],
  /** How strongly the silhouette dominates the underlying material. */
  opacity: 0.94,
  /** Re-evaluate which hostiles are inside the cone at this rate, Hz. Material
   *  swaps are a traverse, so they must not happen every frame. */
  evalHz: 12,
};

const _v = new THREE.Vector3();
const _fwd = new THREE.Vector3();

export class Thermal {
  constructor(ctx, system) {
    this.ctx = ctx;
    this.system = system;
    this.active = false;
    this.painted = new Set(); // enemies currently swapped
    this._saved = new WeakMap(); // mesh -> { material, renderOrder }
    this._acc = 0;
    this._material = null;
    this.paintedCount = 0;
  }

  get config() {
    return this.system.config.thermal;
  }

  onConfigChanged() {
    if (!this.config.enabled) this._clearAll();
  }

  _mat() {
    if (this._material) return this._material;
    const c = TUNING.colour;
    this._material = new THREE.MeshBasicMaterial({
      color: new THREE.Color(c[0], c[1], c[2]),
      toneMapped: true,
      fog: false,
      transparent: true,
      opacity: TUNING.opacity,
      // Depth *test* on, depth *write* off: the world still occludes the
      // silhouette, but being in the transparent queue is what lets it draw after
      // (and therefore through) smoke.
      depthTest: true,
      depthWrite: false,
    });
    return this._material;
  }

  /* --------------------------------- frame --------------------------------- */

  present(dt) {
    const cfg = this.config;
    if (!cfg.enabled) {
      if (this.painted.size) this._clearAll();
      return;
    }

    const up = this._opticUp();
    if (!up) {
      this.active = false;
      if (this.painted.size) this._clearAll();
      return;
    }
    this.active = true;

    this._acc += dt;
    if (this._acc < 1 / TUNING.evalHz) return;
    this._acc = 0;
    this._evaluate();
  }

  _opticUp() {
    const ws = this.ctx.weapons;
    if (ws && Number.isFinite(ws.adsProgress)) return ws.adsProgress >= TUNING.adsThreshold;
    return !!this.ctx.player?.ads;
  }

  _evaluate() {
    const cfg = this.config;
    const cam = this.ctx.camera;
    cam.getWorldDirection(_fwd);
    const cosHalf = Math.cos((cfg.halfAngle * Math.PI) / 180);
    const range2 = cfg.range * cfg.range;

    const list = this.ctx.ai?.enemies;
    const want = this._want ?? (this._want = new Set());
    want.clear();

    if (Array.isArray(list)) {
      for (let i = 0; i < list.length; i++) {
        const e = list[i];
        if (!e || e.dead || !e.position || !e.model?.root) continue;
        _v.set(e.position.x, e.position.y + 1.0, e.position.z).sub(cam.position);
        const d2 = _v.lengthSq();
        if (d2 > range2 || d2 < 1e-4) continue;
        _v.multiplyScalar(1 / Math.sqrt(d2));
        if (_v.dot(_fwd) < cosHalf) continue;
        // Occlusion is *not* tested here on purpose. Doing it with a raycast would
        // be a coarse, per-body approximation of something the depth buffer solves
        // exactly and per pixel a few milliseconds later.
        want.add(e);
      }
    }

    for (const e of this.painted) if (!want.has(e)) this._unpaint(e);
    for (const e of want) if (!this.painted.has(e)) this._paint(e);
    this.paintedCount = this.painted.size;
  }

  _paint(enemy) {
    const root = enemy.model?.root;
    if (!root) return;
    const mat = this._mat();
    const flash = enemy.model.flash;
    root.traverse((o) => {
      if (!o.isMesh || o === flash) return;
      if (this._saved.has(o)) return;
      this._saved.set(o, { material: o.material, renderOrder: o.renderOrder });
      o.material = mat;
      o.renderOrder = TUNING.renderOrder;
    });
    this.painted.add(enemy);
  }

  _unpaint(enemy) {
    const root = enemy.model?.root;
    if (root) {
      root.traverse((o) => {
        if (!o.isMesh) return;
        const s = this._saved.get(o);
        if (!s) return;
        o.material = s.material;
        o.renderOrder = s.renderOrder;
        this._saved.delete(o);
      });
    }
    this.painted.delete(enemy);
  }

  _clearAll() {
    for (const e of [...this.painted]) this._unpaint(e);
    this.painted.clear();
    this.paintedCount = 0;
    this.active = false;
  }

  report() {
    return {
      enabled: !!this.config.enabled,
      opticUp: this.active,
      painted: this.paintedCount,
      halfAngle: this.config.halfAngle,
      /** The rule, so it is discoverable from the running game and not only from
       *  a comment in a file nobody opens. */
      rule: 'walls occlude the highlight; smoke does not',
    };
  }

  dispose() {
    this._clearAll();
    this._material?.dispose();
    this._material = null;
  }
}
